import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  CardMedia,
  Typography,
  IconButton,
  CardContent,
  Tooltip,
} from "@mui/material";
import { ShoppingCart, FavoriteBorder, Favorite } from "@mui/icons-material";
import { StoreContext } from "../StoreContext/StoreContext";
import UtilToastMessages from "../../Utility/UtilToastMessages/UtilToastMessages";
import { OrderCreateApi, OrderUpdateApi } from "../../Utility/UtilApi/UtilApi";
import { buttonColor, fontFamilyall, primaryColor } from "../../UtilColorPalate/UtilColorPalate";

const ProductList = ({ filteredProducts, setHoveredProductId, currencyCode }) => {
  const [hoveredProductId, setHoveredProductIdLocal] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [wishlist, setWishlist] = useState({});
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState("");
  const navigate = useNavigate();

  const {
    hasExistingOrder,
    OrderRecordId,
    dealLineItems,
    hasExistingWishList,
    wishListRecordId,
    wishListItems,
    fetchCurrentDeals,
    isLoggedIn,
    localCartItems,
    updateLocalCart,
    localWishlist,
    updateLocalWishlist,
    getAndUpdateCartCount,
  } = useContext(StoreContext);

 useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top on mount
  }, []);

  // Load wishlist from localStorage on mount if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      const storedWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
      setWishlist(storedWishlist.reduce((acc, item) => ({ ...acc, [item.id]: true }), {}));
    }
  }, [isLoggedIn]);

  // Sync wishlist state with wishListItems on mount (for logged-in users)
  useEffect(() => {
    if (isLoggedIn && hasExistingWishList && wishListItems.length > 0) {
      const wishlistFromApi = {};
      wishListItems.forEach((item) => {
        const productId = item.product.id || item.product;
        wishlistFromApi[productId] = true;
      });
      setWishlist(wishlistFromApi);
    }
  }, [isLoggedIn, hasExistingWishList, wishListItems]);

  // Sync local storage when localWishlist changes (for unauthenticated users)
  useEffect(() => {
    if (!isLoggedIn) {
      localStorage.setItem("wishlist", JSON.stringify(localWishlist));
    }
  }, [localWishlist, isLoggedIn]);

  // Image handling functions (unchanged)
  const cycleImages = (productId, maxImages) => {
    setCurrentImageIndex((prev) => ({
      ...prev,
      [productId]: (prev[productId] || 0) + 1 >= maxImages ? 0 : (prev[productId] || 0) + 1,
    }));
  };

  const handleHover = (productId) => {
    setHoveredProductId(productId);
    setHoveredProductIdLocal(productId);
    if (!currentImageIndex[productId]) {
      setCurrentImageIndex((prev) => ({ ...prev, [productId]: 0 }));
    }
  };

  const handleRest = () => {
    setHoveredProductId(null);
    setHoveredProductIdLocal(null);
  };

  useEffect(() => {
    let intervalId;
    if (hoveredProductId) {
      const product = filteredProducts.find((p) => p.id === hoveredProductId);
      const validImages = [
        product.imageone,
        product.imagetwo,
        product.imagethree,
        product.imagefour,
        product.imagefive,
      ].filter((img) => img && img.trim() !== "");
      const maxImages = validImages.length;

      if (maxImages > 0) {
        intervalId = setInterval(() => {
          cycleImages(hoveredProductId, maxImages);
        }, 1000);
      }
    }
    return () => clearInterval(intervalId);
  }, [hoveredProductId, filteredProducts]);

  const getCurrentImage = (product) => {
    const images = [
      product.imageone,
      product.imagetwo,
      product.imagethree,
      product.imagefour,
      product.imagefive,
    ].filter((img) => img && img.trim() !== "");

    if (images.length === 0) {
      return "https://via.placeholder.com/230";
    }

    const index =
      hoveredProductId === product.id ? (currentImageIndex[product.id] || 0) % images.length : 0;
    return images[index];
  };

  const createDeal = async (product, type) => {
    const generateName = () => `${type}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const payload = {
      order: {
        name: generateName(),
        type: type,
      },
      deallineitems: [
        {
          name: product.name,
          product: product.id,
          quantity: 1,
          unitpriceexclvat: product.unitpriceexclvat,
          vat: product.vat || 0,
          discount: product.discount || 0,
        },
      ],
    };

    try {
      const response = await OrderCreateApi(payload);
      setToastMessage(response.message || `${type} created and product added!`);
      setToastColor(response.success ? "success" : "warning");
      setSnackbarOpen(true);
      await fetchCurrentDeals();
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
      setToastMessage(error?.response?.data?.message || "Failed to create order");
      setToastColor("error");
      setSnackbarOpen(true);
    }
  };

  const updateDeal = async (product, type, recordId, existingItems, isAdding = true) => {
    if (!recordId) {
      console.error(`${type} record ID is missing`);
      setToastMessage(`Error: No ${type} ID available.`);
      setToastColor("error");
      setSnackbarOpen(true);
      return;
    }

    let updatedLineItems = existingItems.map((item) => ({
      recordid: item.recordid,
      name: item.name,
      product: item.product.id || item.product,
      quantity: item.quantity,
      unitpriceexclvat: item.unitpriceexclvat,
      vat: item.vat,
      discount: item.discount || 0,
    }));

    const existingLineItemIndex = updatedLineItems.findIndex(
      (item) => item.product === product.id
    );

    if (isAdding) {
      if (existingLineItemIndex !== -1) {
        updatedLineItems[existingLineItemIndex].quantity += 1;
      } else {
        updatedLineItems.push({
          name: product.name,
          product: product.id,
          quantity: 1,
          unitpriceexclvat: product.unitpriceexclvat,
          vat: product.vat || 0,
          discount: product.discount || 0,
        });
      }

      const payload = {
        order: {
          recordid: recordId,
          type: type,
          stage: "In Progress",
        },
        deallineitems: updatedLineItems,
      };

      try {
        const response = await OrderUpdateApi(payload);
        setToastMessage(response.message || `Product added to ${type}!`);
        setToastColor(response.success ? "success" : "warning");
        setSnackbarOpen(true);
        await fetchCurrentDeals();
      } catch (error) {
        console.error(`Error updating ${type}:`, error);
        setToastMessage(`Failed to update ${type}.`);
        setToastColor("error");
        setSnackbarOpen(true);
      }
    } else {
      if (existingLineItemIndex === -1) return;

      const itemToRemove = updatedLineItems[existingLineItemIndex];
      updatedLineItems = updatedLineItems.filter((item) => item.product !== product.id);

      const payload = {
        order: {
          recordid: recordId,
          type: type,
          stage: "In Progress",
        },
        deallineitems: updatedLineItems,
        remove: [itemToRemove.recordid],
      };

      try {
        const response = await OrderUpdateApi(payload);
        setToastMessage(response.message || `Product removed from ${type}!`);
        setToastColor(response.success ? "success" : "warning");
        setSnackbarOpen(true);
        await fetchCurrentDeals();
      } catch (error) {
        console.error(`Error removing from ${type}:`, error);
        setToastMessage(`Failed to remove from ${type}.`);
        setToastColor("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleWishlistToggle = async (productId) => {
    const product = filteredProducts.find((p) => p.id === productId);
    const isAddingToWishlist = !wishlist[productId];

    if (!isLoggedIn) {
      const wishlistItem = {
        id: product.id,
        name: product.name,
        unitpriceexclvat: product.unitpriceexclvat,
        vat: product.vat || 0,
        discount: product.discount || 0,
        quantity: 1,
      };

      if (isAddingToWishlist) {
        updateLocalWishlist((prev) => {
          const exists = prev.find((item) => item.id === productId);
          if (!exists) {
            const newWishlist = [...prev, wishlistItem];
            setWishlist((prevWishlist) => ({ ...prevWishlist, [productId]: true }));
            return newWishlist;
          }
          return prev;
        });
      } else {
        updateLocalWishlist((prev) => {
          const newWishlist = prev.filter((item) => item.id !== productId);
          setWishlist((prevWishlist) => ({ ...prevWishlist, [productId]: false }));
          return newWishlist;
        });
      }
    } else {
      try {
        if (isAddingToWishlist) {
          if (!hasExistingWishList) {
            await createDeal(product, "Wish List");
          } else {
            await updateDeal(product, "Wish List", wishListRecordId, wishListItems, true);
          }
        } else {
          await updateDeal(product, "Wish List", wishListRecordId, wishListItems, false);
        }
        setWishlist((prev) => ({ ...prev, [productId]: isAddingToWishlist }));
      } catch (error) {
        console.error("Error in handleWishlistToggle:", error);
      }
    }
  };

  const handleAddToCart = async (product) => {
    if (!isLoggedIn) {
      const cartItem = {
        id: product.id,
        name: product.name,
        unitpriceexclvat: product.unitpriceexclvat,
        vat: product.vat || 0,
        discount: product.discount || 0,
        quantity: 1,
      };

      const existingItem = localCartItems.find((item) => item.id === product.id);
      let updatedCart;

      if (existingItem) {
        updatedCart = localCartItems.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      } else {
        updatedCart = [...localCartItems, cartItem];
      }

      updateLocalCart(updatedCart);
      setToastMessage("Product added to cart!");
      setToastColor("success");
      setSnackbarOpen(true);
    } else {
      try {
        if (!hasExistingOrder) {
          await createDeal(product, "Order");
        } else {
          await updateDeal(product, "Order", OrderRecordId, dealLineItems);
        }
        setToastMessage("Product added to cart!");
        setToastColor("success");
        setSnackbarOpen(true);
      } catch (error) {
        console.error("Error in handleAddToCart:", error);
        setToastMessage("Failed to add product to cart.");
        setToastColor("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleGoToCart = () => {
    navigate("/cart");
  };

  const isProductInCart = (productId) => {
    if (!isLoggedIn) {
      return localCartItems.some((item) => item.id === productId);
    }
    return hasExistingOrder && dealLineItems.some(
      (item) => (item.product.id || item.product) === productId
    );
  };

  return (
    <Grid item xs={12} md={10}>
      <Grid container spacing={3}>
        {filteredProducts.map((product) => (
          <Grid item xs={6} sm={4} md={3} key={product.id}>
            <Card
              sx={{
                fontFamily: fontFamilyall,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                position: "relative",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
                overflow: "hidden",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.2)",
                },
              }}
              onMouseEnter={() => handleHover(product.id)}
              onMouseLeave={handleRest}
            >
              <Link
                to={`/product/${product.id}`}
                state={{ product, wishlist: wishlist[product.id] || false, currencyCode }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <CardMedia
                  component="img"
                  image={getCurrentImage(product)}
                  alt={product.name}
                  sx={{
                    objectFit: "cover",
                    width: "100%",
                    height: "230px",
                    transition: "transform 0.3s ease, opacity 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                />
              </Link>

              {product?.discount && product.discount > 0 ?(
                <Typography
                  sx={{
                    fontFamily: fontFamilyall,
                    position: "absolute",
                    top: "10px",
                    left: "10px",
                    backgroundColor: "#ff4444",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                    animation: "pulse 1.5s infinite",
                    "@keyframes pulse": {
                      "0%": { transform: "scale(1)" },
                      "50%": { transform: "scale(1.05)" },
                      "100%": { transform: "scale(1)" },
                    },
                    zIndex: 1,
                  }}
                >
                  {product.discount}% OFF
                </Typography>
              ): ""}

              <CardContent
                sx={{
                  textAlign: "center",
                  padding: "16px",
                  position: "relative",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: fontFamilyall,
                    fontWeight: "600",
                    color: "#333",
                    marginBottom: "8px",
                  }}
                >
                  {product?.name}
                </Typography>
  {product?.discount > 0 ? (
                  <>
                    <Typography
                      color="textSecondary"
                      sx={{
                        fontFamily: fontFamilyall,
                        fontSize: "16px",
                        textDecoration: "line-through",
                        color: "red",
                        marginBottom: "4px",
                      }}
                    >
                      {currencyCode}
                      {product?.unitpriceexclvat}
                    </Typography>
                    <Typography
                      color="black"
                      fontWeight="bold"
                      sx={{
                        fontFamily: fontFamilyall,
                        fontSize: "18px",
                        color: "#8c7aa2",
                        marginBottom: "10px",
                      }}
                    >
                      {currencyCode}
                      {product?.unitpriceincldiscount}
                    </Typography>
                  </>
                ) : (
                  <Typography
                    color="black"
                    fontWeight="bold"
                    sx={{
                      fontFamily: fontFamilyall,
                      fontSize: "18px",
                      color: "#8c7aa2",
                      marginBottom: "10px",
                    }}
                  >
                    {currencyCode}
                    {product?.unitpriceinclvat}
                  </Typography>
                )}
                <br />
                <div
                  style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    right: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Tooltip title={wishlist[product.id] ? "Remove from Wishlist" : "Add to Wishlist"}>
                    <IconButton
                      sx={{ color: wishlist[product.id] ? "#d32f2f" : "#d32f2f" }}
                      onClick={() => handleWishlistToggle(product.id)}
                    >
                      {wishlist[product.id] ? <Favorite /> : <FavoriteBorder />}
                    </IconButton>
                  </Tooltip>

                  {isProductInCart(product.id) ? (
                    <Typography
                                            component="span"
                                            sx={{
                                              backgroundColor: "#4CAF50", // Light green shade
                                              fontFamily: fontFamilyall,
                                              color: "#fff",
                                              textTransform: "none",
                                              padding: {
                                                xs: "4px 10px",
                                                sm: "5px 15px",
                                              },
                                              borderRadius: "4px",
                                              fontSize: {
                                                xs: "0.75rem",
                                                sm: "0.875rem",
                                              },
                                              minWidth: {
                                                xs: "80px",
                                                sm: "100px",
                                              },
                                              display: "inline-block",
                                            }}
                                          >
                                            Added to Cart
                                          </Typography>
                  ) : (
                    <Tooltip title="Add to Cart">
                      <IconButton
                        sx={{
                          color: primaryColor,
                          padding: {
                            xs: "6px",
                            sm: "8px",
                          },
                        }}
                        onClick={() => handleAddToCart(product)}
                      >
                        <ShoppingCart />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <UtilToastMessages
        isOpen={isSnackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        data={toastMessage}
        severity={toastColor}
      />
    </Grid>
  );
};

export default ProductList;